import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useToasts } from 'react-toast-notifications';
import { Box, Button, Modal, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import T from 'i18n';
import clsx from 'clsx';
import { Controller, useForm } from 'react-hook-form';
import { faEdit, faTrash, faUpload, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import fastEqual from 'react-fast-compare';
import { CustomViewerButton, SimpleTooltip } from 'features/common';
import moment from 'moment';
import TagsSelect from './TagsSelect';
import { useSelector } from 'react-redux';
import { selectUserId, selectUserName } from 'common/selectors';
import { cleanItemText } from 'common/utils/textUtils';

const useStyles = makeStyles(() => ({
  noBorder: {
    border: 'none',
  },
  uploadIcon: {
    padding: '0.2rem',
    borderRadius: '50% !important',
    fontSize: '2rem',
    color: 'white',
    margin: '0.5rem',
    cursor: 'pointer',
    backgroundColor: '#4567e5',
  },
  disabled: {
    backgroundColor: '#A5A4A1',
  },
  error: {
    backgroundColor: 'red',
  },
  markButton: {
    padding: '0.25rem',
    position: 'relative',
    transition: 'opacity 150ms, visibility 0ms 150ms',
    whiteSpace: 'nowrap',
    backgroundColor: '#4567e5',
    border: 'none',
    boxShadow: '0 4px 6px 0 #666',
    color: 'white',
    textAlign: 'center',
    opacity: '0.85',
    margin: '0.1rem',
    borderRadius: '10%',
    a: {
      color: 'white',
      '&:hover': {
        textDecoration: 'underline',
        cursor: 'pointer',
      },
    },
    "&[type='button']:hover": {
      boxShadow: '0 4px 10px 0 #666',
      opacity: 1,
    },
  },
  markButtonWhite: {
    color: 'black',
    backgroundColor: 'white',
    boxShadow: '0 4px 6px 0 #666',
    '&:focus': {
      boxShadow: '0 4px 6px 0 #666',
    },
  },
  markButtonOrange: {
    backgroundColor: '#ff9200',
    boxShadow: '0 4px 6px 0 #666',
    '&:focus': {
      boxShadow: '0 4px 6px 0 #666',
    },
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'white',
    borderRadius: '0.5rem',
    width: 200,
    height: 100,
    padding: '1rem',
  },
}));

type TimelineType = {
  id: string;
  name: string;
  eventName: string;
};

type CommentInputPropsType = {
  onClick: (data: any) => void;
  comment?: string;
  selectedTags?: any[];
  selectedTimeline?: TimelineType;
  update?: boolean;
  deleteAnnotation: any;
  createdBy: any;
  selectMark: any;
  page: any;
  shared: any;
  onSharedClick: any;
  itemText?: string;
};

export default ({
  onClick,
  comment: defaultComment = '',
  selectedTags = [],
  selectedTimeline,
  update = false,
  deleteAnnotation,
  createdBy,
  selectMark,
  page,
  shared,
  onSharedClick,
  itemText = '',
}: CommentInputPropsType) => {
  const classes = useStyles();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }, // this line is an absolute must in order to subscribe to getFieldState
  } = useForm({
    defaultValues: {
      comment: update ? defaultComment : defaultComment,
      tags: selectedTags,
    },
  });
  // Enable edit mode by default when editing tags (no timeline)
  const isTagsContext = update && !selectedTimeline?.id;
  const [edit, setEdit] = useState(!!isTagsContext);
  const [areYouSureDelete, setAreYouSureDelete] = useState(false);
  const [areYouSureLeave, setAreYouSureLeave] = useState<any | null>(false);
  const prevDefaultCommentRef = useRef(defaultComment);
  const prevSelectedTagsRef = useRef(selectedTags);
  const prevItemTextRef = useRef(itemText);
  const [isTagsMenuOpen, setIsTagsMenuOpen] = useState<boolean>(false);
  const [suppressReopenOnEnter, setSuppressReopenOnEnter] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLInputElement>();
  const userId = useSelector(selectUserId);
  const userName = useSelector(selectUserName);
  const { addToast } = useToasts();

  // Reset form values when the comment or tags props actually change (by value, not reference)
  useEffect(() => {
    const commentChanged = prevDefaultCommentRef.current !== defaultComment;
    const tagsChanged = !fastEqual(prevSelectedTagsRef.current, selectedTags);
    const itemTextChanged = prevItemTextRef.current !== itemText;

    if (commentChanged || tagsChanged || itemTextChanged) {
      reset({
        comment: defaultComment,
        tags: selectedTags,
      });
      prevDefaultCommentRef.current = defaultComment;
      prevSelectedTagsRef.current = selectedTags;
      prevItemTextRef.current = itemText;
    }
  }, [defaultComment, selectedTags, itemText, update, reset]);

  // When editing tags by default, focus the comment field and place caret at end
  useEffect(() => {
    if (isTagsContext && edit && commentRef.current) {
      setTimeout(() => {
        if (commentRef.current) {
          commentRef.current.focus();
          const caretPosition = commentRef.current.value?.length || 0;
          commentRef.current.selectionEnd = caretPosition;
          commentRef.current.selectionStart = caretPosition;
        }
        if (selectMark) selectMark();
      }, 0);
    }
  }, [isTagsContext, edit, selectMark]);

  // Ensure edit mode is re-enabled when reopening in tags context
  useEffect(() => {
    if (isTagsContext) {
      setEdit(true);
    }
  }, [isTagsContext, defaultComment, selectedTags, itemText]);

  const closePopup = useCallback(() => {
    if (page) {
      page.dispatchEvent(
        new MouseEvent('mousedown', {
          view: window,
          bubbles: true,
          cancelable: true,
        }),
      );
      page.dispatchEvent(
        new MouseEvent('mouseup', {
          view: window,
          bubbles: true,
          cancelable: true,
        }),
      );
    }
  }, [page]);

  const doSave = useCallback(() => {
    if (!isDirty || !isValid) return;
    const save = handleSubmit((data) => {
      const maybePromise: any = onClick(data);
      const afterSave = () => {
        setEdit(false);
        closePopup();
      };
      if (maybePromise && typeof maybePromise.then === 'function') {
        return maybePromise
          .then(() => {
            if (isTagsContext) {
              addToast(T.translate('viewing.tagsUpdatedSuccess'), {
                appearance: 'success',
                autoDismiss: true,
                autoDismissTimeout: 1000,
              });
            }
            afterSave();
          })
          .catch(() => {
            if (isTagsContext) {
              addToast(T.translate('viewing.tagsUpdatedFailure'), {
                appearance: 'error',
                autoDismiss: true,
                autoDismissTimeout: 1000,
              });
            }
          });
      } else {
        if (isTagsContext) {
          addToast(T.translate('viewing.tagsUpdatedSuccess'), {
            appearance: 'success',
            autoDismiss: true,
          });
        }
        afterSave();
      }
    });
    save();
  }, [isDirty, isValid, handleSubmit, onClick, isTagsContext, addToast, closePopup]);

  const handleMouseDown = useCallback(
    (event: any) => {
      if (update && isTagsContext && isDirty && isValid) {
        event.stopPropagation();
        event.preventDefault();
        doSave();
        return false;
      }
      if ((update && isDirty) || areYouSureDelete) {
        if (!areYouSureDelete && isDirty) {
          setAreYouSureLeave(event.target);
        }
        event.stopPropagation();
        event.preventDefault();
        return false;
      } else {
        closePopup();
        document.removeEventListener('mousedown', handleMouseDown, true);
      }
    },
    [isDirty, isValid, update, isTagsContext, doSave, closePopup, areYouSureDelete],
  );

  // Handle Escape/Enter keys
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (isTagsMenuOpen) {
          const target = event.target as HTMLElement | null;
          const isInsideTagsMenu = !!target?.closest('.MuiPaper-root,[role="listbox"]');
          const isTextInput = target?.tagName === 'INPUT' || target?.getAttribute('role') === 'textbox';
          if (isInsideTagsMenu || isTextInput) {
            // let TagsSelect handle Enter (e.g., create tag)
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          return; // ignore Enter elsewhere while tags menu is open
        }
        if (suppressReopenOnEnter) {
          event.preventDefault();
          event.stopPropagation();
          setSuppressReopenOnEnter(false);
          if (update && isTagsContext && isDirty && isValid) {
            doSave();
          }
          return;
        }
        if (update && isTagsContext && isDirty && isValid) {
          event.preventDefault();
          doSave();
          return;
        }
      }
      if (event.key === 'Escape') {
        // Check if we have unsaved changes or are in delete confirmation
        if (!isTagsMenuOpen && update && isTagsContext && isDirty && isValid) {
          event.preventDefault();
          doSave();
          return;
        } else if ((update && isDirty) || areYouSureDelete) {
          // If there are unsaved changes, show confirmation dialog
          if (!areYouSureDelete && isDirty) {
            setAreYouSureLeave(event.target);
          }
          // If in delete confirmation, cancel the delete
          else if (areYouSureDelete) {
            setAreYouSureDelete(false);
          }
        } else {
          // No unsaved changes, close the popup
          closePopup();
          document.removeEventListener('mousedown', handleMouseDown, true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    update,
    isDirty,
    areYouSureDelete,
    closePopup,
    handleMouseDown,
    doSave,
    isTagsContext,
    isValid,
    isTagsMenuOpen,
    suppressReopenOnEnter,
  ]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div
        style={{ position: 'fixed', bottom: 0, top: 0, right: 0, left: 0, zIndex: -1 }}
        onClick={handleMouseDown}
      />
      <div ref={containerRef} style={{ width: '100%', height: '100%', zIndex: 1 }}>
        <Modal
          open={areYouSureDelete || !!areYouSureLeave}
          container={containerRef.current}
          sx={{ position: 'absolute', '& .MuiModal-backdrop': { position: 'absolute' } }}
        >
          <Box className={classes.modal}>
            <Typography
              id="modal-modal-title"
              variant="h6"
              component="h2"
              style={{
                textAlign: 'center',
              }}
            >
              {areYouSureDelete ? 'Delete. Are you sure?' : 'Leave without saving?'}
            </Typography>

            <div
              style={{ width: '100%', padding: '1rem', display: 'flex', justifyContent: 'center' }}
            >
              <Button
                style={{ margin: '0.2rem', background: '#4567e5', color: 'white' }}
                onClick={() => {
                  if (areYouSureDelete) deleteAnnotation();
                  else {
                    closePopup();
                    document.removeEventListener('mousedown', handleMouseDown, true);
                  }
                }}
              >
                {T.translate('generic.yes')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (areYouSureDelete) setAreYouSureDelete(false);
                  else setAreYouSureLeave(null);
                }}
                style={{ margin: '0.2rem', borderColor: '#4567e5' }}
              >
                {T.translate('generic.no')}
              </Button>
            </div>
          </Box>
        </Modal>
        <div
          style={{
            display: 'flex',
            flexDirection: update ? 'column' : 'row',
            ...(update && { padding: '1rem' }),
          }}
        >
          <div style={{ position: 'absolute', right: '0.5rem', top: '0.5rem' }}>
            {update && (!edit || isTagsContext) && (createdBy?.id === userId || !createdBy) && (
              <SimpleTooltip
                title={T.translate(shared ? 'viewing.unshareToTeam' : 'viewing.shareToTeam')}
              >
                <CustomViewerButton
                  onClick={onSharedClick}
                  className={clsx(classes.markButton, shared ? classes.markButtonOrange : null)}
                  icon={faShareAlt}
                />
              </SimpleTooltip>
            )}
            {!edit && update && !isTagsContext && (createdBy?.id === userId || !createdBy) && (
              <SimpleTooltip title={T.translate('viewing.editAnnotation')}>
                <CustomViewerButton
                  onClick={() => {
                    setEdit(true);
                    // Reset form to ensure latest values are loaded
                    reset({
                      comment: defaultComment,
                      tags: selectedTags,
                    });
                    if (commentRef.current) {
                      setTimeout(() => {
                        if (commentRef.current) {
                          commentRef.current.focus();
                          const caretPosition = commentRef.current.value?.length || 0;
                          commentRef.current.selectionEnd = caretPosition;
                          commentRef.current.selectionStart = caretPosition;
                        }
                      }, 0);
                    }
                    if (selectMark) selectMark();
                  }}
                  className={classes.markButton}
                  icon={faEdit}
                />
              </SimpleTooltip>
            )}
            {edit && update && !isTagsContext && (
              <SimpleTooltip title={T.translate('viewing.saveAnnotation')}>
                <CustomViewerButton
                  onClick={
                    isValid && isDirty
                      ? handleSubmit((data) => {
                          const maybePromise: any = onClick(data);
                          const afterSave = () => {
                            setEdit(false);
                            closePopup();
                            document.removeEventListener('mousedown', handleMouseDown, true);
                          };
                          if (maybePromise && typeof maybePromise.then === 'function') {
                            maybePromise
                              .then(() => {
                                if (isTagsContext) {
                                  addToast(T.translate('viewing.tagsUpdatedSuccess'), {
                                    appearance: 'success',
                                    autoDismiss: true,
                                    autoDismissTimeout: 1000,
                                  });
                                }
                                afterSave();
                              })
                              .catch(() => {
                                if (isTagsContext) {
                                  addToast(T.translate('viewing.tagsUpdatedFailure'), {
                                    appearance: 'error',
                                    autoDismiss: true,
                                    autoDismissTimeout: 1000,
                                  });
                                }
                              });
                          } else {
                            if (isTagsContext) {
                              addToast(T.translate('viewing.tagsUpdatedSuccess'), {
                                appearance: 'success',
                                autoDismiss: true,
                              });
                            }
                            afterSave();
                          }
                        })
                      : undefined
                  }
                  className={clsx(classes.markButton, {
                    [classes.disabled]: !isValid || !isDirty,
                  })}
                  icon={faUpload}
                />
              </SimpleTooltip>
            )}
            {update && (createdBy?.id === userId || !createdBy) && (
              <SimpleTooltip title={T.translate('viewing.deleteAnnotation')}>
                <CustomViewerButton
                  onClick={() => setAreYouSureDelete(true)}
                  className={classes.markButton}
                  icon={faTrash}
                />
              </SimpleTooltip>
            )}
          </div>
          {update && <Typography variant="h6">{createdBy?.name || userName}</Typography>}
          {update && (
            <Typography style={{ color: 'gray' }} variant="subtitle2">
              {`Created on ${moment(createdBy?.date).utc().format('DD MMM HH:mm')}`}
            </Typography>
          )}
          {update && (
            <Typography style={{ marginTop: '1rem' }} variant="h6" component="h6">
              {'Comment'}
            </Typography>
          )}
          <Controller
            name="comment"
            control={control}
            rules={{ required: !update }}
            render={({ field }: any) => {
              return (
                <TextField
                  {...field}
                  sx={{ padding: '8px 0', flexGrow: 1 }}
                  inputRef={commentRef}
                  autoFocus
                  placeholder={
                    !edit && update
                      ? T.translate('0 comments')
                      : T.translate('case.annotationNotePlaceholder')
                  }
                  margin="none"
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isTagsMenuOpen) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    if (e.key === 'Enter' && isDirty && isValid && !isTagsMenuOpen) {
                      e.preventDefault();
                      handleSubmit((data) => {
                        const maybePromise: any = onClick(data);
                        const afterSave = () => {
                          setEdit(false);
                          closePopup();
                          document.removeEventListener('mousedown', handleMouseDown, true);
                        };
                        if (maybePromise && typeof maybePromise.then === 'function') {
                          maybePromise
                            .then(() => {
                              if (isTagsContext) {
                                addToast(T.translate('viewing.tagsUpdatedSuccess'), {
                                  appearance: 'success',
                                  autoDismiss: true,
                                  autoDismissTimeout: 1000,
                                });
                              }
                              afterSave();
                            })
                            .catch(() => {
                              if (isTagsContext) {
                                addToast(T.translate('viewing.tagsUpdatedFailure'), {
                                  appearance: 'error',
                                  autoDismiss: true,
                                  autoDismissTimeout: 1000,
                                });
                              }
                            });
                        } else {
                          if (isTagsContext) {
                            addToast(T.translate('viewing.tagsUpdatedSuccess'), {
                              appearance: 'success',
                              autoDismiss: true,
                              autoDismissTimeout: 1000,
                            });
                          }
                          afterSave();
                        }
                      })();
                    }
                  }}
                  InputProps={{
                    readOnly: !edit && update,
                    classes: { notchedOutline: classes.noBorder },
                  }}
                  multiline={update}
                  maxRows={5}
                />
              );
            }}
          />
          {selectedTimeline?.id ? (
            <div>
              <Typography style={{ marginTop: '1rem' }} variant="h6" component="h6">
                {T.translate('case.eventName')}
              </Typography>
              <Typography style={{ color: 'gray' }} variant="subtitle2">
                {selectedTimeline.eventName}
              </Typography>
            </div>
          ) : (
            <>
              {update && (
                <>
                  <Typography style={{ marginTop: '1rem' }} variant="h6" component="h6">
                    {'Tags'}
                  </Typography>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }: any) => {
                      return (
                        <TagsSelect
                          {...field}
                          placeholder={
                            !edit && update ? T.translate('0 tags') : T.translate('Select tags')
                          }
                          readOnly={!edit && update}
                          onOpen={() => setIsTagsMenuOpen(true)}
                          onClosed={() => {
                            // After menu closes, keep Enter handling within the popup
                            // so pressing Enter will save instead of reopening the menu
                            if (selectMark) selectMark();
                            setIsTagsMenuOpen(false);
                            setSuppressReopenOnEnter(true);
                          }}
                          // Do not submit via Enter while the tags menu is open
                        />
                      );
                    }}
                  />
                </>
              )}
            </>
          )}
          {update && isTagsContext && (
            <div style={{ alignSelf: 'flex-end' }}>
              <Button size="small" variant="contained" disabled={!isDirty} onClick={doSave}>
                {T.translate('generic.update')}
              </Button>
            </div>
          )}
          {!update && (
            <SimpleTooltip title={T.translate('viewing.createAnnotation')}>
              <CustomViewerButton
                onClick={handleSubmit((data) => {
                  onClick(data);
                })}
                className={clsx(classes.markButton, {
                  [classes.disabled]: !isValid,
                })}
                style={{
                  margin: '0.5rem',
                  width: '2.5rem',
                  borderRadius: '10%',
                  height: '2.5rem',
                  alignSelf: 'center',
                }}
                icon={faUpload}
              />
            </SimpleTooltip>
          )}
        </div>
      </div>
    </div>
  );
};
